import React from 'react';

import { Divider, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useAuthenticatedUser } from 'components/hooks/dataHooks';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getOneOnOneInstancePagePath } from 'screens/oneOnOneRabbit/helpers';
import { check1on1V2Enabled } from 'utils/helpers/featureFlags';

import { OneOnOneIcon, Play } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/newIllustrations/noOneOnOne.svg';

import { goToOneOnOneInstancePage } from 'constants/helper';
import { routes } from 'constants/routes';

import { Avatar } from 'uiToolkitV1/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup';
import { Button } from 'uiToolkitV1/Button';
import { DateComp } from 'uiToolkitV1/DateComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { EmptySummaryCard } from '../../EmptyCard/EmptySummaryCard';
import { MySummaryCardTemplate } from '../MySummaryCardTemplate';
import { useMySummaryOneOnOne, useMySummaryOOO } from './hooks';

const Container = styled(Flex)`
  padding: 12px 8px;
  &:hover {
    background: ${(props) => props.theme.colors.neutral.F6FAFE};
    & .highlightTextOnHover {
      color: #1565bc;
    }
  }
  cursor: pointer;
`;
const ButtonContainer = styled('div')`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${Container}:hover & {
    visibility: visible;
    display: flex;
  }
`;

const StyledAvatarGroup = styled(AvatarGroup)`
  display: flex;
  ${Container}:hover & {
    visibility: visible;
    display: none;
  }
`;
const ViewDetailsButton = styled(Button)`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${Container}:hover & {
    visibility: visible;
    display: flex;
  }
`;

const OneOnOneMySummaryRow = ({ id, metrics, dueDate, employees = [], showDivider }) => {
  const dispatch = useDispatch();

  const { displayName, profilePhoto } = employees[0] ?? {};

  const { completed, total } = metrics ?? {};

  const history = useHistory();

  const handleStart = ({ id }) => {
    goToOneOnOneInstancePage(history, id, 'HomePage');
  };

  return (
    <>
      <Container onClick={() => handleStart({ id })}>
        <Flex width="100%">
          <Flex flexDir="column" width="100%">
            <Flex alignItems="center" sx={{ marginBottom: '8px' }}>
              <Avatar size={24} src={profilePhoto} name={displayName} />
              <TextBodyMedium color="text.g1" ml="8px" className="highlightTextOnHover">
                {displayName}
              </TextBodyMedium>
            </Flex>
            <Flex alignItems="center" minH="28px">
              <TextBodyMedium display="flex">
                Next Schedule: &nbsp;
                <DateComp
                  date={dueDate || new Date()}
                  isRelativeDateFormat
                  enableRelativeFormatDistanceColor
                  textComponent={TextBodyMedium}
                  forceRelativeDateFormat
                  showDateInBrackets
                />
              </TextBodyMedium>

              <ButtonContainer className="showOnHover">
                <ViewDetailsButton
                  onClick={() => {
                    handleStart({ id });
                  }}
                  variant="rowButtonOutlined"
                  StartIcon={Play}
                  className="showOnHover"
                >
                  Start
                </ViewDetailsButton>
              </ButtonContainer>
            </Flex>
            <Flex alignItems="center" mt="2px">
              <TextBodyMedium color="functional.C62C02" bold display="flex">
                {completed} of {total} completed
              </TextBodyMedium>
            </Flex>
          </Flex>
        </Flex>
      </Container>
      {showDivider && <Divider color="neutral.CFD7DF" />}
    </>
  );
};

const OOOMySummaryRow = ({ employee, completionPercentage, nextOneOnOne, showDivider }) => {
  const { displayName, profilePhoto } = employee?.value?.employeeMiniProfile ?? {};

  const nextInstanceTS = nextOneOnOne?.value?.nextInstanceTS * 1000;

  const masterId = employee?.value?.oneOnOneMasterId;
  const instanceId = nextOneOnOne?.value?.latestInstanceId;
  const oneOnOneInstanceExists = typeof masterId === 'number' && typeof instanceId === 'number';

  const { completionPercentageValue, pastInstancesCount } = completionPercentage?.value ?? {};
  const showCompletionPercentage = typeof completionPercentageValue === 'number';
  const completionColor =
    completionPercentageValue === 0 ? '#C62C02' : completionPercentageValue > 50 ? '#44812F' : '#B96400';

  const history = useHistory();

  const handleStart = () => {
    if (oneOnOneInstanceExists) {
      const route = getOneOnOneInstancePagePath({
        masterId,
        instanceId,
      });
      history.push(route);
    }
  };

  return (
    nextInstanceTS && (
      <>
        <Container
          onClick={handleStart}
          style={{
            cursor: oneOnOneInstanceExists ? 'pointer' : 'default',
          }}
        >
          <Flex width="100%">
            <Flex flexDir="column" width="100%">
              <Flex alignItems="center" sx={{ marginBottom: '8px' }}>
                <Avatar size={24} src={profilePhoto} name={displayName} />
                <TextBodyMedium color="text.g1" ml="8px" className="highlightTextOnHover">
                  {displayName}
                </TextBodyMedium>
              </Flex>
              <Flex alignItems="center" minH="28px">
                <TextBodyMedium
                  display="flex"
                  color="text.g2"
                  sx={{
                    p: {
                      color: '#666666',
                      fontWeight: 'normal',
                    },
                  }}
                >
                  Next Schedule:&nbsp;
                  <DateComp
                    date={nextInstanceTS}
                    isRelativeDateFormat
                    enableRelativeFormatDistanceColor={false}
                    textComponent={TextBodyMedium}
                    forceRelativeDateFormat
                    showDateInBrackets
                  />
                </TextBodyMedium>
              </Flex>
              <Flex alignItems="center" mt="2px">
                <TextBodyMedium color={completionColor} bold display="flex">
                  {showCompletionPercentage && (
                    <>
                      {completionPercentageValue}% of last {pastInstancesCount} completed
                    </>
                  )}
                </TextBodyMedium>
              </Flex>
            </Flex>
          </Flex>
        </Container>
        {showDivider && <Divider color="neutral.CFD7DF" />}
      </>
    )
  );
};

const OneOnOneMySummaryCard = ({ employeeId }) => {
  const {
    user: { id, teams },
  } = useAuthenticatedUser();
  const { data: oldOneOnOneData, isLoading: oldOneOnOneIsLoading } = useMySummaryOneOnOne({
    limit: 3,
    offset: 0,
    employeeId: employeeId ?? id,
    enabled: !check1on1V2Enabled(),
  });

  const { data: oooData, isLoading: oooIsLoading } = useMySummaryOOO({
    limit: 3,
    enabled: check1on1V2Enabled(),
  });

  const isLoading = check1on1V2Enabled() ? oooIsLoading : oldOneOnOneIsLoading;
  const data = check1on1V2Enabled() ? oooData : oldOneOnOneData;

  if (isLoading) {
    return (
      <MySummaryCardTemplate Icon={OneOnOneIcon} heading="1:1s" showViewAllButton={false}>
        <div style={{ marginTop: '16px' }}>
          <SkeletonLoader />
        </div>
      </MySummaryCardTemplate>
    );
  }

  if (!data?.length) {
    return (
      <EmptySummaryCard
        Icon={OneOnOneIcon}
        heading="1:1s"
        description="1:1s are the best way to give/receive coaching and feedback. Set up your 1:1s now and track the upcoming one right here."
        illustrationSrc={NoDataImg}
        redirectUrl={routes.oneOnOne.audit}
      />
    );
  }

  if (check1on1V2Enabled()) {
    return (
      <MySummaryCardTemplate
        Icon={OneOnOneIcon}
        heading="1:1s"
        viewAllText="View All 1:1s"
        viewAllHref={routes.oneOnOne.audit}
      >
        {data?.map(({ employee, completionPercentage, nextOneOnOne }, index) => (
          <OOOMySummaryRow
            employee={employee}
            completionPercentage={completionPercentage}
            nextOneOnOne={nextOneOnOne}
            showDivider
            key={index}
          />
        ))}
      </MySummaryCardTemplate>
    );
  }

  return (
    <MySummaryCardTemplate
      Icon={OneOnOneIcon}
      heading="1:1s"
      viewAllText="View All 1:1s"
      viewAllHref={routes.oneOnOne.audit}
    >
      {data?.map(({ id, metrics, dueDate, employees }, index) => (
        <OneOnOneMySummaryRow metrics={metrics} dueDate={dueDate} employees={employees} showDivider id={id} key={id} />
      ))}
    </MySummaryCardTemplate>
  );
};

export { OneOnOneMySummaryCard };
